// A browser room as a brand-new seed (2026-10-09): the arrangement it
// uploads, and where each audio clip's audio comes from. Pure.
//
// A room made in the browser from nothing has no seed to commit to, so it
// goes up through /api/uploads like any other new seed (shared/upload/
// publish-seed.ts) rather than through a jam. Its audio is of two kinds:
//
//   POOLED — a pool object already stored (a clip from the producer's
//   library, a piece of another seed's material): the key stays on the
//   clip, nothing is sent, and /complete names it so the seed takes a
//   reference to it.
//
//   BYTES — anything else (a take recorded in this tab, `local/…`; a legacy
//   per-seed object): the clip goes up keyless with its bytes, and the
//   server stamps the key it is stored under.
//
// Indexes address the Snapshot `buildArrangement` produced, so they are read
// off that Snapshot, never recomputed from the room's own order.

import { buildArrangement } from "../publish-bridge.js";
import type { Snapshot } from "../seed-history.js";
import type { RoomStateResponse } from "../sync/intents.js";
import type { PooledClip } from "./publish-seed.js";

/** An audio clip whose bytes must be sent, from the key the room plays. */
export interface ClipNeedingBytes {
  list: "arrangement" | "session";
  trackIndex: number;
  clipIndex: number;
  sourceKey: string;
}

export interface RoomSeedPlan {
  snapshot: Snapshot;
  pooled: PooledClip[];
  needsBytes: ClipNeedingBytes[];
}

type BuildableRoom = Parameters<typeof buildArrangement>[0] & {
  tracks: RoomStateResponse["tracks"];
  clips: RoomStateResponse["clips"];
};

export function planRoomSeed(room: BuildableRoom, tempo: number, isPool: (key: string) => boolean): RoomSeedPlan {
  // Every key maps to itself: the walk below decides which stay.
  const identity = new Map<string, string>();
  for (const c of room.clips) if (c.audioKey) identity.set(c.audioKey, c.audioKey);
  const snapshot = buildArrangement(room, null, tempo, identity);

  const pooled: PooledClip[] = [];
  const needsBytes: ClipNeedingBytes[] = [];
  snapshot.audioTracks.forEach((track, trackIndex) => {
    const walk = (list: ClipNeedingBytes["list"], clips: Array<{ audioKey?: string | null }>): void => {
      clips.forEach((clip, clipIndex) => {
        const key = clip.audioKey;
        if (!key) return;
        if (isPool(key)) {
          pooled.push({ list, trackIndex, clipIndex, key });
        } else {
          needsBytes.push({ list, trackIndex, clipIndex, sourceKey: key });
          clip.audioKey = null;
        }
      });
    };
    walk("arrangement", track.arrangementClips);
    walk("session", track.sessionClips ?? []);
  });
  return { snapshot, pooled, needsBytes };
}

/** Whether a room holds anything a seed could play: audio, or a MIDI note. */
export function roomHasContent(room: Pick<BuildableRoom, "clips">): boolean {
  return room.clips.some((c) => (c.clipKind === "midi" ? (c.notes?.length ?? 0) > 0 : c.audioKey !== null));
}
